import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Eraser, Check } from 'lucide-react';
interface SignatureCaptureProps {
  onSignatureChange: (signature: string | null) => void;
  disabled?: boolean;
  width?: number;
  height?: number;
}
export function SignatureCapture({
  onSignatureChange,
  disabled = false,
  width = 400,
  height = 150
}: SignatureCaptureProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [lastPoint, setLastPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Set up canvas
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Draw signature line
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(20, height - 30);
    ctx.lineTo(width - 20, height - 30);
    ctx.strokeStyle = '#d1d5db';
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#1a1a1a';
  }, [width, height]);
  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY
      };
    } else {
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    }
  };
  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    const coords = getCoordinates(e);
    if (!coords) return;
    setIsDrawing(true);
    setLastPoint(coords);
  };
  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !lastPoint) return;
    const coords = getCoordinates(e);
    if (!coords) return;
    ctx.beginPath();
    ctx.moveTo(lastPoint.x, lastPoint.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setLastPoint(coords);
    setHasSignature(true);
  };
  const stopDrawing = () => {
    if (isDrawing && hasSignature) {
      const canvas = canvasRef.current;
      if (canvas) {
        const signature = canvas.toDataURL('image/png');
        onSignatureChange(signature);
      }
    }
    setIsDrawing(false);
    setLastPoint(null);
  };
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Redraw signature line
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(20, height - 30);
    ctx.lineTo(width - 20, height - 30);
    ctx.strokeStyle = '#d1d5db';
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#1a1a1a';
    setHasSignature(false);
    onSignatureChange(null);
  };
  return <div className="space-y-3" data-id="element-1068">
      <div className="flex items-center justify-between" data-id="element-1069">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1070">
          Draw your signature below
        </label>
        {hasSignature && <motion.button initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} onClick={clearSignature} disabled={disabled} className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50" data-id="element-1071">
            <Eraser className="h-4 w-4" data-id="element-1072" />
            <span data-id="element-1073">Clear</span>
          </motion.button>}
      </div>

      <div className={`relative border-2 rounded-lg overflow-hidden transition-colors ${disabled ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800' : hasSignature ? 'border-green-300 dark:border-green-700 bg-white dark:bg-gray-900' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900'}`} data-id="element-1074">
        <canvas ref={canvasRef} width={width} height={height} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} className={`w-full touch-none ${disabled ? 'cursor-not-allowed' : 'cursor-crosshair'}`} style={{
        maxWidth: '100%',
        height: 'auto',
        aspectRatio: `${width}/${height}`
      }} data-id="element-1075" />

        {!hasSignature && !disabled && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-1076">
            <p className="text-gray-400 dark:text-gray-500 text-sm" data-id="element-1077">
              Sign here with your mouse or finger
            </p>
          </div>}

        {hasSignature && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="absolute top-2 right-2" data-id="element-1078">
            <div className="bg-green-100 dark:bg-green-900/30 p-1 rounded-full" data-id="element-1079">
              <Check className="h-4 w-4 text-green-600 dark:text-green-400" data-id="element-1080" />
            </div>
          </motion.div>}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1081">
        Your signature will be legally binding once you complete the signing
        process.
      </p>
    </div>;
}